import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { DashboardStats, Order, StoreProfile } from "@/types/store";
import { Store, DollarSign } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }
    fetchStats();
  }, [user, isAdmin, authLoading, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all data in parallel for better performance
      const [
        pendingStoresResponse,
        storeProfilesResponse,
        ordersResponse,
        recentOrdersResponse
      ] = await Promise.all([
        // Pending stores
        supabase
          .from("store_profiles")
          .select("id, store_name, created_at")
          .eq("approval_status", "pending")
          .order("created_at", { ascending: false }),

        // Store profiles
        supabase
          .from("store_profiles")
          .select("*")
          .returns<StoreProfile[]>(),

        // All orders for totals
        supabase
          .from("orders")
          .select("total_amount, commission_amount")
          .returns<Order[]>(),

        // Recent orders with store details
        supabase
          .from("orders")
          .select(`
            id,
            created_at,
            total_amount,
            commission_amount,
            florist_id,
            store_profiles (
              store_name
            )
          `)
          .order("created_at", { ascending: false })
          .limit(5)
          .returns<Order[]>()
      ]);

      // Check for errors
      if (pendingStoresResponse.error) throw pendingStoresResponse.error;
      if (storeProfilesResponse.error) throw storeProfilesResponse.error;
      if (ordersResponse.error) throw ordersResponse.error;
      if (recentOrdersResponse.error) throw recentOrdersResponse.error;

      // Process the data
      const storeProfiles = storeProfilesResponse.data || [];
      const allOrders = ordersResponse.data || [];
      const recentOrders = recentOrdersResponse.data || [];
      const pendingStores = pendingStoresResponse.data || [];

      // Calculate totals
      const totalRevenue = allOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
      const totalCommission = allOrders.reduce((sum, order) => sum + (order.commission_amount || 0), 0);
      const activeFlorists = storeProfiles.filter(store => store.approval_status === 'approved').length;

      // Format recent orders
      const formattedOrders = recentOrders.map(order => ({
        id: order.id,
        created_at: order.created_at,
        total_amount: order.total_amount || 0,
        commission_amount: order.commission_amount || 0,
        florist_id: order.florist_id,
        customer_id: order.customer_id || '',
        status: order.status || 'completed',
        store_profiles: order.store_profiles,
        store_name: order.store_profiles?.store_name || "Unknown Store"
      })) as Order[];

      setStats({
        pendingApplications: pendingStores.length,
        totalFlorists: storeProfiles.length,
        activeFlorists,
        totalOrders: allOrders.length,
        totalCustomers: 0, // This would need a separate query to count unique customers
        totalRevenue,
        totalCommission,
        recentOrders: formattedOrders,
        pendingStores
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError("Failed to fetch dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="Dashboard">
        <div className="flex justify-center items-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout currentPage="Dashboard">
        <div className="flex flex-col items-center justify-center h-screen">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={fetchStats}>Try Again</Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="Dashboard">
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-[#4A4F41]">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Welcome to your admin dashboard</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Applications
              </CardTitle>
              <div className="h-8 w-8 rounded-full bg-[#967F81]/10 flex items-center justify-center">
                <Store className="h-4 w-4 text-brand-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.pendingApplications || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Awaiting approval
              </p>
            </CardContent>
          </Card>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Florists
              </CardTitle>
              <div className="h-8 w-8 rounded-full bg-[#967F81]/10 flex items-center justify-center">
                <Store className="h-4 w-4 text-brand-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalFlorists || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.activeFlorists || 0} active
              </p>
            </CardContent>
          </Card>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Revenue
              </CardTitle>
              <div className="h-8 w-8 rounded-full bg-[#967F81]/10 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-brand-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats?.totalRevenue || 0)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.totalOrders || 0} orders
              </p>
            </CardContent>
          </Card>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Commission
              </CardTitle>
              <div className="h-8 w-8 rounded-full bg-[#967F81]/10 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-brand-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats?.totalCommission || 0)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Platform earnings
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-medium">Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats?.recentOrders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <div className="font-medium">{(order as any).store_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">
                        {formatCurrency(order.total_amount)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Commission: {formatCurrency(order.commission_amount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium">Pending Approvals</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/stores?status=pending")}
              >
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats?.pendingStores.map((store) => (
                  <div
                    key={store.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <div className="font-medium">{store.store_name}</div>
                      <div className="text-sm text-muted-foreground">
                        Applied: {new Date(store.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/stores/${store.id}`)}
                    >
                      Review
                    </Button>
                  </div>
                ))}
                {(stats?.pendingStores.length || 0) === 0 && (
                  <div className="text-center text-muted-foreground py-4">
                    No pending approvals
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}